// The roadmap as a mind map, drawn by mind-elixir (MIT) — the owner's steer on
// the first cut: use a good open-source mind-map package rather than a
// hand-rolled graph. mind-elixir gives the XMind look people know: root in the
// middle, thick coloured branches per task force balanced left and right,
// initiatives as topics with health / people chips, sub-actions folded under
// them, and cross-topic arrows for blocker → blocked. Read-only here (no
// editing, context menu or toolbar); zoom with the wheel, drag to pan, click a
// topic to open the same drill-down drawer the table and timeline use.
// Thin wrapper: the library owns the DOM inside the host; React owns lifecycle.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MindElixir, { type NodeObj, type Theme } from 'mind-elixir';
import 'mind-elixir/style.css';
import type { InitiativeState } from '../../services/roadmapReports';
import type { MandateLookup, TeamColors } from './roadmapViewUtils';
import {
  buildMindMapData,
  countActions,
  countRelated,
  DEFAULT_MIND_MAP_OPTIONS,
  HEALTH_TAG_STYLE,
  actionKeyOfMeta,
  initiativeKeyOfMeta,
  teamBuckets,
  type MindMapMeta,
  type MindMapOptions,
} from './roadmapMindMapData';
import styles from './RoadmapMindMap.module.css';

interface Props {
  initiatives: InitiativeState[];
  teamColors?: TeamColors;
  onSelect?: (key: string, section?: undefined, action?: string) => void;
  /** The mandate registry (v10): a team with areas branches into them. */
  mandates?: MandateLookup;
}

/** mind-elixir's light theme with the roadmap's palette: team colours drive
 * the main branches (branchColor per team wins over the palette order). */
function roadmapTheme(colors: string[]): Theme {
  return {
    ...MindElixir.THEME,
    name: 'roadmap',
    palette: colors.length > 0 ? colors : MindElixir.THEME.palette,
    cssVar: {
      ...MindElixir.THEME.cssVar,
      '--bgcolor': '#ffffff',
      '--root-bgcolor': '#0f172a',
      '--root-color': '#ffffff',
      '--root-border-color': '#0f172a',
      '--root-radius': '14px',
      '--main-radius': '10px',
      '--main-bgcolor': '#ffffff',
      '--main-color': '#0f172a',
      '--color': '#0f172a',
      '--selected': '#2563eb',
      '--node-gap-x': '40px',
      '--node-gap-y': '10px',
      '--main-gap-x': '70px',
      '--main-gap-y': '24px',
      '--topic-padding': '4px 8px',
    },
  };
}

export function RoadmapMindMap({ initiatives, teamColors = {}, onSelect, mandates = {} }: Props) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const meRef = useRef<MindElixir | null>(null);
  const [opts, setOpts] = useState<MindMapOptions>(DEFAULT_MIND_MAP_OPTIONS);

  const data = useMemo(() => buildMindMapData(initiatives, teamColors, opts, mandates), [initiatives, teamColors, opts, mandates]);
  const buckets = useMemo(() => teamBuckets(initiatives.filter((i) => i.status !== 'dropped'), teamColors), [initiatives, teamColors]);
  const actionCount = useMemo(() => countActions(initiatives), [initiatives]);
  const relatedCount = useMemo(() => countRelated(initiatives), [initiatives]);
  const dependsCount = data.arrows?.filter((a: { id: string }) => a.id.startsWith('dep:')).length ?? 0;

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !data.nodeData.children?.length) return;
    meRef.current?.destroy();
    let me: MindElixir;
    try {
      me = new MindElixir({
        el: host,
        direction: MindElixir.SIDE,
        editable: false,
        contextMenu: false,
        toolBar: false,
        keypress: false,
        allowUndo: false,
        alignment: 'nodes',
        scaleMin: 0.2,
        scaleMax: 2.5,
        theme: roadmapTheme(buckets.map((b) => b.color)),
      });
      me.init(data);
      me.scaleFit();
    } catch (err) {
      // A drawing failure must never take the page down — the table carries
      // the same facts. Log so it is diagnosable.
      console.error('Roadmap mind map failed to render', err);
      return;
    }
    // A click on an initiative or one of its sub-actions drills in; the root
    // and team topics just select (mind-elixir's own highlight).
    me.bus.addListener('selectNodes', (nodes: NodeObj[]) => {
      const meta = nodes[0]?.metadata as MindMapMeta | undefined;
      const key = initiativeKeyOfMeta(meta);
      const actionKey = actionKeyOfMeta(meta);
      if (key) { if (actionKey) onSelect?.(key, undefined, actionKey); else onSelect?.(key); }
    });
    meRef.current = me;
    return () => {
      me.destroy();
      meRef.current = null;
    };
  }, [data, buckets, onSelect]);

  const fit = useCallback(() => meRef.current?.scaleFit(), []);
  const center = useCallback(() => meRef.current?.toCenter(), []);

  if (initiatives.filter((i) => i.status !== 'dropped').length === 0) return null;

  return (
    <section className={styles.panel} aria-label="Roadmap mind map">
      <div className={styles.head}>
        <h2>Mind map</h2>
        <span className={styles.controls}>
          <button type="button" className={styles.btn} onClick={fit} title="Fit the whole map in view">
            Fit
          </button>
          <button type="button" className={styles.btn} onClick={center} title="Back to the root at 100%">
            Centre
          </button>
          {actionCount > 0 && (
            <button
              type="button"
              className={`${styles.btn} ${opts.expandActions ? styles.btnOn : ''}`}
              onClick={() => setOpts((o) => ({ ...o, expandActions: !o.expandActions }))}
              aria-pressed={opts.expandActions}
              title="Sub-actions fold under their initiative; open or close them all (each initiative also has its own ▸ toggle)"
            >
              {opts.expandActions ? `Sub-actions open (${actionCount})` : `Sub-actions closed (${actionCount})`}
            </button>
          )}
          {relatedCount > 0 && (
            <button
              type="button"
              className={`${styles.btn} ${opts.showRelated ? styles.btnOn : ''}`}
              onClick={() => setOpts((o) => ({ ...o, showRelated: !o.showRelated }))}
              aria-pressed={opts.showRelated}
              title="Grey two-way arrows between initiatives Merlin linked by shared cause or topic"
            >
              {opts.showRelated ? `Related links on (${relatedCount})` : `Related links off (${relatedCount})`}
            </button>
          )}
        </span>
        <span className={styles.legend}>
          {buckets.map((b) => (
            <span key={b.name} className={styles.legendItem}>
              <span className={styles.branch} style={{ background: b.color }} /> {b.name}
            </span>
          ))}
          <span className={styles.legendItem} title="The chip on an initiative is its schedule health">
            <span className={styles.chip} style={HEALTH_TAG_STYLE.on_track}>On track</span>
            <span className={styles.chip} style={HEALTH_TAG_STYLE.at_risk}>At risk</span>
            <span className={styles.chip} style={HEALTH_TAG_STYLE.late}>Late</span>
            <span className={styles.chip} style={HEALTH_TAG_STYLE.no_target}>No target</span>
          </span>
          {dependsCount > 0 && (
            <span className={styles.legendItem}>
              <span className={styles.arrowDepends} /> blocks →
            </span>
          )}
        </span>
      </div>
      <p className={styles.hint}>
        The roadmap in the middle, one branch per task force, initiatives along each branch with their health and
        people, sub-actions folded underneath. Click an initiative or sub-action to drill in. Scroll to zoom, drag to
        pan, ▸ on a topic folds it.
      </p>
      <div ref={hostRef} className={styles.host} data-testid="roadmap-mindmap" />
    </section>
  );
}
